import { isEqual, isFunction } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useForm, useFormState } from "react-final-form";
import RffForm from "./RffForm";

const RffFormChanges = ({
  children,
  // `id` puts the <form> under a known id so a submit button OUTSIDE it (a
  // dialog footer) can reach it with `form={id}`.
  id,
  // Fires whenever the form flips between clean and dirty — an outside footer
  // needs it to know when to enable its Save.
  onHasChangesChange,
  hasChangesContent,
  initialValues,
  suppressFooter,
  onCancelText,
  loading,
  formSpy = false,
  onCancel,
  onSubmit,
  hasChangesContentPosition = "bottom", // top | bottom | null
  // Keep `hasChangesContent` mounted while the form is clean too — for a
  // floating save pill that animates its own exit / "Saved" hold and so
  // must outlive the last change. The content decides what to show.
  persistChangesContent = false,
  submitText = "Save Changes",
  submitButtonSpacing = 4,
  footerStartContent,
  disabledWhenNoChanges = true,
  sendWholeObject = false,
  sx,
}) => {
  const [formKey] = useState(0);
  const { changes, setChanges, hasChanges, getPayload } = useFormChanges();

  useEffect(() => {
    if (isFunction(onHasChangesChange)) onHasChangesChange(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  const handleOnSubmit = async (values, form) => {
    const changedValues = getPayload(sendWholeObject ? values : null);

    if (isFunction(onSubmit)) {
      onSubmit(changedValues, { changedValues, form, values, changes });
      return;
    }
  };

  return (
    <RffForm
      key={formKey}
      id={id}
      suppressFooter={suppressFooter}
      formSpy={formSpy}
      onCancel={!hasChanges ? null : onCancel}
      onCancelText={onCancelText}
      initialValues={initialValues}
      loading={loading}
      sx={sx}
      disabled={!hasChanges && disabledWhenNoChanges}
      submitText={submitText}
      footerStartContent={footerStartContent}
      onSubmit={handleOnSubmit}
    >
      <FormChangeTracker
        onChangesUpdate={setChanges}
        initialValues={initialValues}
      />
      {(hasChanges || persistChangesContent) &&
      hasChangesContentPosition === "top"
        ? hasChangesContent
        : null}
      {children}

      {(hasChanges || persistChangesContent) &&
      hasChangesContentPosition === "bottom"
        ? hasChangesContent
        : null}
    </RffForm>
  );
};

const FormChangeTracker = ({ onChangesUpdate, initialValues }) => {
  const form = useForm();
  const { values } = useFormState();
  const [, setChangedValues] = useState({});
  const initialValuesRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (initialValues) {
      initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));

      if (!isInitialized) {
        setIsInitialized(true);
      }

      setChangedValues({});
      if (onChangesUpdate) {
        onChangesUpdate({});
      }
    }
  }, [initialValues, onChangesUpdate]);

  useEffect(() => {
    if (!isInitialized || !initialValuesRef.current || !values) return;

    const changes = {};
    const compareValues = (current, initial, path = "") => {
      const allKeys = new Set([
        ...Object.keys(current || {}),
        ...Object.keys(initial || {}),
      ]);

      allKeys.forEach((key) => {
        const currentPath = path ? `${path}.${key}` : key;
        const currentValue = current?.[key];
        const initialValue = initial?.[key];

        if (currentValue === undefined && initialValue === undefined) {
          return;
        }

        if (
          typeof currentValue === "object" &&
          currentValue !== null &&
          !Array.isArray(currentValue)
        ) {
          compareValues(currentValue, initialValue || {}, currentPath);
        } else if (Array.isArray(currentValue)) {
          if (!isEqual(currentValue, initialValue)) {
            changes[currentPath] = currentValue;
          }
        } else if (currentValue !== initialValue) {
          changes[currentPath] = currentValue;
        }
      });
    };

    compareValues(values, initialValuesRef.current);
    setChangedValues(changes);

    if (onChangesUpdate) {
      onChangesUpdate(changes);
    }
  }, [values, isInitialized, onChangesUpdate]);

  useEffect(() => {
    const unsubscribe = form.subscribe(
      ({ pristine }) => {
        if (pristine && isInitialized) {
          setChangedValues({});
          if (onChangesUpdate) {
            onChangesUpdate({});
          }
        }
      },
      { pristine: true },
    );

    return unsubscribe;
  }, [form, isInitialized, onChangesUpdate]);

  return null;
};

// Hook to use form changes
const useFormChanges = () => {
  const [changes, setChanges] = useState({});
  const hasChanges = Object.keys(changes).length > 0;

  return {
    changes,
    setChanges,
    hasChanges,
    /**
     * Build the payload from tracked changes.
     * @param {Object|null} fullValues - When provided (sendWholeObject mode),
     *   if any nested key within a top-level object changed, the entire
     *   top-level object from fullValues is included in the payload.
     */
    getPayload: (fullValues) => {
      // First, build the standard nested payload from changed paths
      const payload = {};
      Object.entries(changes).forEach(([path, value]) => {
        const keys = path.split(".");
        let current = payload;
        keys.forEach((key, index) => {
          if (index === keys.length - 1) {
            current[key] = value;
          } else {
            current[key] = current[key] || {};
            current = current[key];
          }
        });
      });

      // If sendWholeObject mode, replace partial nested objects with
      // the full object from current form values
      if (fullValues) {
        Object.keys(payload).forEach((key) => {
          const payloadValue = payload[key];
          const fullValue = fullValues[key];

          if (
            payloadValue &&
            typeof payloadValue === "object" &&
            !Array.isArray(payloadValue) &&
            fullValue &&
            typeof fullValue === "object" &&
            !Array.isArray(fullValue)
          ) {
            payload[key] = { ...fullValue };
          }
        });
      }

      return payload;
    },
    resetChanges: () => setChanges({}),
  };
};

export default RffFormChanges;

// import { isEqual, isFunction } from "lodash";
// import { useEffect, useRef, useState } from "react";
// import { useForm, useFormState } from "react-final-form";
// import RffForm from "./RffForm";

// const RffFormChanges = ({
//   children,
//   hasChangesContent,
//   initialValues,
//   // suppressFooter = true,
//   suppressFooter,
//   onCancelText,
//   loading,
//   formSpy = false,
//   // formSpy = true,
//   onCancel,
//   onSubmit,
//   submitText = "Save Changes",
//   submitButtonSpacing = 4,
//   footerStartContent,
//   disabledWhenNoChanges = true,
//   sx,
// }) => {
//   const [formKey, setFormKey] = useState(0); // Key to force form re-initialization
//   const { changes, setChanges, hasChanges, getPayload, resetChanges } =
//     useFormChanges();

//   const handleOnSubmit = async (values, form) => {
//     const changedValues = getPayload();

//     if (isFunction(onSubmit)) {
//       onSubmit(changedValues, { changedValues, form, values, changes });
//       return;
//     }
//   };

//   return (
//     <RffForm
//       key={formKey} // Force re-initialization when profile loads
//       suppressFooter={suppressFooter}
//       formSpy={formSpy}
//       onCancel={!hasChanges ? null : onCancel}
//       onCancelText={onCancelText}
//       // suppressFooter={hasChanges ? undefined : () => null}
//       initialValues={initialValues}
//       // formSpy
//       loading={loading}
//       sx={sx}
//       disabled={!hasChanges && disabledWhenNoChanges}
//       submitText={submitText}
//       footerStartContent={footerStartContent}
//       onSubmit={handleOnSubmit}
//       // Show save button only when there are changes
//       // renderFooter={hasChanges ? undefined : () => null}
//     >
//       <FormChangeTracker
//         onChangesUpdate={setChanges}
//         initialValues={initialValues} // Pass initial values to tracker
//       />

//       {children}
//       {hasChanges ? hasChangesContent : null}
//     </RffForm>
//   );
// };

// const FormChangeTracker = ({ onChangesUpdate, initialValues }) => {
//   const form = useForm();
//   const { values, pristine, dirty } = useFormState();
//   const [changedValues, setChangedValues] = useState({});
//   const initialValuesRef = useRef(null);
//   const [isInitialized, setIsInitialized] = useState(false);

//   // Update initial values reference when initialValues prop changes
//   useEffect(() => {
//     if (initialValues) {
//       // Always update the reference when initialValues change
//       initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));

//       if (!isInitialized) {
//         setIsInitialized(true);
//       }

//       // Reset changes when initial values change (e.g., after save)
//       setChangedValues({});
//       if (onChangesUpdate) {
//         onChangesUpdate({});
//       }
//     }
//   }, [initialValues, onChangesUpdate]); // Remove isInitialized from dependencies

//   // Track changes only after initialization
//   useEffect(() => {
//     if (!isInitialized || !initialValuesRef.current || !values) return;

//     const changes = {};
//     const compareValues = (current, initial, path = "") => {
//       // Handle cases where current has keys that initial doesn't
//       const allKeys = new Set([
//         ...Object.keys(current || {}),
//         ...Object.keys(initial || {}),
//       ]);

//       allKeys.forEach((key) => {
//         const currentPath = path ? `${path}.${key}` : key;
//         const currentValue = current?.[key];
//         const initialValue = initial?.[key];

//         if (currentValue === undefined && initialValue === undefined) {
//           return;
//         }

//         if (
//           typeof currentValue === "object" &&
//           currentValue !== null &&
//           !Array.isArray(currentValue)
//         ) {
//           compareValues(currentValue, initialValue || {}, currentPath);
//         } else if (Array.isArray(currentValue)) {
//           if (!isEqual(currentValue, initialValue)) {
//             changes[currentPath] = currentValue;
//           }
//         } else if (currentValue !== initialValue) {
//           changes[currentPath] = currentValue;
//         }
//       });
//     };

//     compareValues(values, initialValuesRef.current);
//     setChangedValues(changes);

//     if (onChangesUpdate) {
//       onChangesUpdate(changes);
//     }
//   }, [values, isInitialized, onChangesUpdate]);

//   // Reset tracking when form is reset
//   useEffect(() => {
//     const unsubscribe = form.subscribe(
//       ({ pristine }) => {
//         if (pristine && isInitialized) {
//           setChangedValues({});
//           if (onChangesUpdate) {
//             onChangesUpdate({});
//           }
//         }
//       },
//       { pristine: true }
//     );

//     return unsubscribe;
//   }, [form, isInitialized, onChangesUpdate]);

//   return null; // This component doesn't render anything
// };

// // Hook to use form changes
// const useFormChanges = () => {
//   const [changes, setChanges] = useState({});
//   const hasChanges = Object.keys(changes).length > 0;

//   return {
//     changes,
//     setChanges,
//     hasChanges,
//     getPayload: () => {
//       // Convert dot notation back to nested object for payload
//       const payload = {};
//       Object.entries(changes).forEach(([path, value]) => {
//         const keys = path.split(".");
//         let current = payload;
//         keys.forEach((key, index) => {
//           if (index === keys.length - 1) {
//             current[key] = value;
//           } else {
//             current[key] = current[key] || {};
//             current = current[key];
//           }
//         });
//       });
//       return payload;
//     },
//     resetChanges: () => setChanges({}),
//   };
// };

// export default RffFormChanges;
